import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, MapPin, Phone, ChevronRight, CheckCircle2 } from 'lucide-react';
import PageShell from './PageShell';
import Breadcrumbs from '../Breadcrumbs';
import { useLeadModal } from '../../lib/leadModalContext';
import { SCHOOL } from '../../lib/constants';

/**
 * Reusable template for program & info pages. Keeps design identity consistent
 * (Tron black + red neon). Each page passes a unique `pageData` prop.
 */
const DeepPage = ({ data }) => {
  const { openLeadModal } = useLeadModal();
  const {
    breadcrumbs = [],
    eyebrow,
    h1,
    tagline,
    intro,
    heroImage,
    heroImageAlt,
    sections = [],
    featureList,
    callouts,
    ctaCopy = 'Book Your Free Trial Class',
    source = 'deep-page',
    bottomNote,
  } = data;

  const handleBook = () => openLeadModal(source);

  return (
    <>
      <Breadcrumbs items={breadcrumbs} />

      {/* HERO */}
      <section className="relative pt-6 sm:pt-10 pb-10 sm:pb-16" data-testid="deeppage-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            {eyebrow && (
              <span className="inline-block font-display uppercase tracking-[0.22em] text-[10px] text-[color:var(--neon-red)] border border-[color:var(--neon-red)]/45 rounded-full px-2.5 py-1 glow-red-sm">
                {eyebrow}
              </span>
            )}
            <h1
              className="mt-4 font-display font-black tracking-tight text-white text-[2.25rem] leading-[1.05] sm:text-5xl lg:text-6xl sm:leading-[1.02]"
              data-testid="deeppage-h1"
            >
              {h1}
            </h1>
            {tagline && (
              <p className="mt-4 font-mono-ui uppercase tracking-[0.18em] text-[11px] sm:text-xs text-[color:var(--neon-cyan)]">
                {tagline}
              </p>
            )}
            {intro && (
              <p className="mt-5 text-white/80 text-[15px] sm:text-lg leading-relaxed max-w-2xl">
                {intro}
              </p>
            )}

            <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <button
                type="button"
                onClick={handleBook}
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 min-h-[52px] px-6 rounded-xl bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] text-white font-display uppercase tracking-[0.14em] text-sm glow-red-md transition-transform active:scale-[0.98]"
                data-testid="deeppage-cta-book"
              >
                <Sparkles className="h-4 w-4" /> {ctaCopy}
              </button>
              <a
                href={SCHOOL.phoneHref}
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 h-12 min-h-[52px] px-5 rounded-xl border border-white/15 text-white hover:bg-white/5 font-display uppercase tracking-[0.14em] text-sm"
                data-testid="deeppage-cta-call"
              >
                <Phone className="h-4 w-4" /> Call {SCHOOL.phone}
              </a>
            </div>

            {featureList && (
              <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {featureList.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2 text-sm text-white/80"
                  >
                    <CheckCircle2 className="h-4 w-4 text-[color:var(--neon-red)] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>

          {heroImage && (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="relative aspect-[4/5] sm:aspect-[3/4] rounded-2xl overflow-hidden panel">
                <img
                  src={heroImage}
                  alt={heroImageAlt || h1}
                  className="absolute inset-0 h-full w-full object-cover"
                  loading="eager"
                  width="600"
                  height="800"
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(to top, rgba(7,8,10,0.80) 0%, rgba(7,8,10,0.20) 40%, transparent 70%)',
                  }}
                />
                <div className="absolute left-5 right-5 bottom-4 text-xs font-mono-ui uppercase tracking-[0.18em] text-white/80">
                  {SCHOOL.locationLabel}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* CALLOUT STRIP */}
      {Array.isArray(callouts) && callouts.length > 0 && (
        <section className="relative py-6 sm:py-8 border-y border-white/8 bg-white/[0.015]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {callouts.map((c) => (
              <div key={c.title} className="flex items-start gap-3" data-testid={`callout-${c.title.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="h-9 w-9 shrink-0 grid place-items-center rounded-xl border border-[color:var(--neon-red)]/45 text-[color:var(--neon-red)] glow-red-sm">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-display uppercase tracking-[0.14em] text-xs text-white">
                    {c.title}
                  </div>
                  <div className="mt-1 text-sm text-white/70 leading-relaxed">{c.body}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* RICH CONTENT SECTIONS */}
      {sections.map((s, i) => (
        <section
          key={s.heading + i}
          className="relative py-14 sm:py-20"
          data-testid={`deeppage-section-${i}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-5"
            >
              {s.eyebrow && (
                <span className="font-display uppercase tracking-[0.22em] text-[11px] text-[color:var(--neon-red)]">
                  {s.eyebrow}
                </span>
              )}
              <h2 className="mt-3 font-display text-2xl sm:text-4xl text-white tracking-tight leading-[1.1]">
                {s.heading}
              </h2>
              {s.sub && (
                <p className="mt-4 text-white/70 leading-relaxed">{s.sub}</p>
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="lg:col-span-7"
            >
              {Array.isArray(s.body) ? (
                <div className="space-y-4 text-white/80 leading-relaxed">
                  {s.body.map((para, j) => (
                    <p key={j}>{para}</p>
                  ))}
                </div>
              ) : (
                <p className="text-white/80 leading-relaxed">{s.body}</p>
              )}
              {Array.isArray(s.bullets) && (
                <ul className="mt-5 space-y-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-white/80">
                      <CheckCircle2 className="h-4 w-4 text-[color:var(--neon-red)] mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          </div>
        </section>
      ))}

      {/* NOTE / FLAG */}
      {bottomNote && (
        <section className="relative pb-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="panel rounded-2xl p-4 sm:p-5 text-xs sm:text-sm text-white/60 leading-relaxed">
              {bottomNote}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="relative py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div
            className="panel rounded-2xl p-6 sm:p-10 lg:p-14 relative overflow-hidden"
            style={{
              boxShadow: '0 0 0 1px rgba(255,42,42,0.35), 0 20px 60px rgba(0,0,0,0.6)',
            }}
          >
            <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div>
                <span className="font-display uppercase tracking-[0.22em] text-[11px] text-[color:var(--neon-red)]">
                  Your Free Trial
                </span>
                <h2 className="mt-2 font-display text-2xl sm:text-3xl text-white tracking-tight">
                  Ready to get on the mat?
                </h2>
                <p className="mt-2 text-white/70 text-sm sm:text-base">
                  Two free classes. Zero pressure. See if it&#39;s a fit.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={handleBook}
                  className="inline-flex items-center justify-center gap-2 h-12 min-h-[52px] px-6 rounded-xl bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] text-white font-display uppercase tracking-[0.14em] text-sm glow-red-md"
                  data-testid="deeppage-footer-book"
                >
                  <Sparkles className="h-4 w-4" /> {ctaCopy}
                  <ChevronRight className="h-4 w-4" />
                </button>
                <a
                  href={SCHOOL.directionsUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center justify-center gap-2 h-12 min-h-[52px] px-5 rounded-xl border border-white/15 text-white hover:bg-white/5 font-display uppercase tracking-[0.14em] text-sm"
                  data-testid="deeppage-footer-directions"
                >
                  <MapPin className="h-4 w-4" /> Visit Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

/** Convenience wrapper that automatically wraps in PageShell. */
export const DeepPageWithShell = ({ data }) => (
  <PageShell>
    <DeepPage data={data} />
  </PageShell>
);

export default DeepPage;
